import {Plugins, withSystem} from '@packages/systems/wfdl';
import {
  TListOfElement,
  functionOf,
  EFunction,
  ECall,
  Expression,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {_$, $} from '@packages/utilities/wfdl-helpers';
import {
  predicate,
  runPredicate,
  TPredicate,
} from '@packages/systems/plugins-utils/Boolean';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {TCaseSensitiveText} from '@packages/systems/plugins/Text';

const TCaseSensitiveTextPredicate = functionOf(
  TCaseSensitiveText,
  TPredicate(TListOfElement)
);

export const plugin = Plugins.PrivilegedPlugin({
  values: {
    RichTextCondition: operand(
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('RichTextChildren'))
      )
    ),
    TextCondition: operand(TListOfElement),

    TextIsSet: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is set'}, TPredicate(TListOfElement)),
      },
      expression: _$('Basic', 'listOfElementsIsSet'),
    },

    TextIsNotSet: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is not set'}, TPredicate(TListOfElement)),
      },
      expression: _$('Basic', 'listOfElementsIsNotSet'),
    },

    TextEquals: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is'}, TCaseSensitiveTextPredicate),
      },
      expression: makeListOfElementsPredicateFromTextPredicate('CaseEquals'),
    },

    TextDoesNotEqual: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is not'}, TCaseSensitiveTextPredicate),
      },
      expression:
        makeListOfElementsPredicateFromTextPredicate('CaseDoesNotEqual'),
    },

    TextContains: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'contains'}, TCaseSensitiveTextPredicate),
      },
      expression: makeListOfElementsPredicateFromTextPredicate('CaseContains'),
    },

    TextDoesNotContain: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'does not contain'},
          TCaseSensitiveTextPredicate
        ),
      },
      expression:
        makeListOfElementsPredicateFromTextPredicate('CaseDoesNotContain'),
    },

    TextStartsWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'starts with'},
          TCaseSensitiveTextPredicate
        ),
      },
      expression:
        makeListOfElementsPredicateFromTextPredicate('CaseStartsWith'),
    },

    TextDoesNotStartWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'does not start with'},
          TCaseSensitiveTextPredicate
        ),
      },
      expression: makeListOfElementsPredicateFromTextPredicate(
        'CaseDoesNotStartWith'
      ),
    },

    TextEndsWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'ends with'}, TCaseSensitiveTextPredicate),
      },
      expression: makeListOfElementsPredicateFromTextPredicate('CaseEndsWith'),
    },

    TextDoesNotEndWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'does not end with'},
          TCaseSensitiveTextPredicate
        ),
      },
      expression:
        makeListOfElementsPredicateFromTextPredicate('CaseDoesNotEndWith'),
    },
  },
});

/**
 * Given a `Predicate<Text>` expression, returns a `Predicate<ListOfElements>`
 * expression.
 */
function asListOfElementsPredicate(textPredicateExpr: Expression): Expression {
  return predicate(
    EFunction({
      param: 'listOfElements',
      body: ECall({
        fun: runPredicate(textPredicateExpr),
        arg: ECall({
          fun: _$('Basic', 'listOfElementsToText'),
          arg: $('listOfElements'),
        }),
      }),
    })
  );
}

/**
 * Given a `X => Predicate<Text>` expression,
 * returns a `X => Predicate<ListOfElements>` expression.
 */
function makeListOfElementsPredicateFromTextPredicate(
  textPredicateName: string
): Expression {
  return EFunction({
    param: 'x',
    body: asListOfElementsPredicate(
      ECall({
        fun: _$('Text', textPredicateName),
        arg: $('x'),
      })
    ),
  });
}
